import * as NomenclatureDocumentsAction from './nomenclature-documents.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {NomenclatureDocument, NomenclatureDocumentSearchParams, NomenclatureDocumentType} from '@accounting/_common/models/nomenclature-document.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {BindDocument, UnbindDocument} from '@documents/organization-document/_store/organization-document.actions';

export const nomenclatureDocumentsFeatureKey = 'nomenclatureDocuments';

export function sortByDate(a: NomenclatureDocument, b: NomenclatureDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<NomenclatureDocument> = createEntityAdapter<NomenclatureDocument>({
    sortComparer: sortByDate
});

export const typedListInitialState: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export interface NomenclatureDocumentsState {
    REVENUE_INVOICE: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    ADVANCE_REPORT: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    GOODS_POSTING: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    SALES_INVOICE: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    GOODS_WRITE_OFF: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    GOODS_REFUND: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    GOODS_TRANSFER: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>,
    CROSS: EntityStateModel<NomenclatureDocument, NomenclatureDocumentSearchParams>
}

export const initialState: NomenclatureDocumentsState = {
    REVENUE_INVOICE: {...typedListInitialState},
    ADVANCE_REPORT: {...typedListInitialState},
    GOODS_POSTING: {...typedListInitialState},
    SALES_INVOICE: {...typedListInitialState},
    GOODS_WRITE_OFF: {...typedListInitialState},
    GOODS_REFUND: {...typedListInitialState},
    GOODS_TRANSFER: {...typedListInitialState},
    CROSS: {...typedListInitialState}
};

export const nomenclatureDocumentsReducer = createReducer(
    initialState,


    on(NomenclatureDocumentsAction.CheckState, (state, action) => {
        if (action.payload.documentType == 'CROSS') {
            if (action.payload.close) {
                return {
                    ...state,
                    CROSS: initialState['CROSS']
                };
            }
            return {...state};
        }

        if (action.payload.modalView) {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeAll({
                    ...state[action.payload.documentType],
                    savedFilter: !action.payload.close ? state[action.payload.documentType].filter : undefined,
                    pagination: state[action.payload.documentType].pagination,
                    filter: action.payload.close ? state[action.payload.documentType].savedFilter : undefined
                })
            });
        } else {
            if (action.payload.close) {
                return ({
                    ...state,
                    [action.payload.documentType]: adapter.setAll(action.payload.items, {
                        ...state[action.payload.documentType],
                        pagination: {...state[action.payload.documentType].pagination, number: 0},
                        componentOpened: false
                    })
                });
            }

            return {...state, [action.payload.documentType]: {...state[action.payload.documentType], componentOpened: true}};
        }
    }),

    on(NomenclatureDocumentsAction.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: {
                ...state[action.payload.documentType],
                listLoading: StoredComponentHelperService.updateListLoading(action.payload, state[action.payload.documentType].ids)
            }
        });
    }),

    on(NomenclatureDocumentsAction.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: action.payload.clearState
                ? adapter.setAll(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : adapter.upsertMany(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined
                }),
        });
    }),

    on(NomenclatureDocumentsAction.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            listLoading: undefined
        }
    })),

    on(NomenclatureDocumentsAction.SetPagination, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            pagination: {
                ...state[action.payload.documentType].pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.payload.documentType].pagination, state[action.payload.documentType].ids?.length) : 0
            }
        }
    })),

    on(NomenclatureDocumentsAction.SetFilter, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            filter: action.payload.filter
        }
    })),

    on(NomenclatureDocumentsAction.SetLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            loading: action.payload.loading
        }
    })),

    on(NomenclatureDocumentsAction.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(NomenclatureDocumentsAction.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state[action.payload.documentType]?.filter, action.payload.item);
        if (savedItemAvailableByFilter) {
            return {
                ...state,
                [action.payload.documentType]: adapter.setOne(action.payload.item, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType]?.itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids)
                    }
                })
            };
        } else {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType]?.itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                    }
                })
            });
        }
    }),

    on(NomenclatureDocumentsAction.DeleteItem, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(NomenclatureDocumentsAction.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                ...state[action.payload.documentType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.documentType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                }
            })
        });
    }),

    on(NomenclatureDocumentsAction.DeleteItemFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false)
        }
    })),

    on(BindDocument, (state, action) => {
        if (!(action.payload.request.documentType && action.payload.request.documentType in NomenclatureDocumentType)) {
            return state;
        }

        const documentType = action.payload.request.documentType as NomenclatureDocumentType;
        return {
            ...state,
            [documentType]: adapter.updateOne({
                id: action.payload.request.itemId,
                changes: {
                    attachmentsExists: true
                }
            }, state[documentType])
        };
    }),

    on(UnbindDocument, (state, action) => {
        if (!(action.payload.request.documentType && action.payload.request.documentType in NomenclatureDocumentType)) {
            return state;
        }

        const documentType = action.payload.request.documentType as NomenclatureDocumentType;
        return {
            ...state,
            [documentType]: adapter.updateOne({
                id: action.payload.request.itemId,
                changes: {
                    attachmentsExists: !action.payload.lastDocumentUnbinded
                }
            }, state[documentType])
        };
    }),

    on(NomenclatureDocumentsAction.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: NomenclatureDocumentType | 'CROSS') => adapter.getSelectors((state: NomenclatureDocumentsState) => state[type]).selectAll;

const isAvailableByFilter = (searchParams: NomenclatureDocumentSearchParams | undefined, item: NomenclatureDocument): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.types && (!item.type || searchParams.types.indexOf(item.type) == -1)) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    if ((searchParams.operative || searchParams.operative === false) && searchParams.operative !== item.operative) {
        return false;
    }

    if (searchParams.company?.id && item.company?.id != searchParams.company?.id) {
        return false;
    }

    if (searchParams.query && !item.comment?.toLowerCase()?.includes(searchParams.query.toLowerCase()) && item.total != searchParams.query) {
        return false;
    }

    if (searchParams.goodsOnly === true && !item?.goods?.length || searchParams.goodsOnly === false && !item?.services?.length) {
        return false;
    }

    if (searchParams.attachmentsExists !== undefined && searchParams.attachmentsExists != item.attachmentsExists) {
        return false;
    }

    return !(searchParams.refundParentType && item.refundParent?.type != searchParams.refundParentType);
};
